import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CloseButton,
    Flex,
    Input,
    Text,
    VStack,
} from '@chakra-ui/react';
import type { SubmittedIdentifierCorrectionRequest } from '../../utils/invoiceKeyIdentifierTeaching';

export interface InvoiceSubmittedIdentifierCorrectionModalProps {
    request: SubmittedIdentifierCorrectionRequest | null;
    vendorName: string;
    invoiceTypeLabel: string;
    isLoading?: boolean;
    error?: string;
    onClose: () => void;
    onConfirm: (correctedValue: string) => void;
}

/**
 * Compact dialog over the invoice detail column. The typed value is saved as
 * an AI hint for the next invoice.
 */
export default function InvoiceSubmittedIdentifierCorrectionModal({
    request,
    vendorName,
    invoiceTypeLabel,
    isLoading = false,
    error = '',
    onClose,
    onConfirm,
}: InvoiceSubmittedIdentifierCorrectionModalProps) {
    useEffect(() => {
        if (!request) return undefined;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !isLoading) onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isLoading, onClose, request]);

    if (!request) return null;

    return (
        <Box
            position="absolute"
            inset={0}
            zIndex={21}
            bg="blackAlpha.500"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={6}
            onClick={() => {
                if (!isLoading) onClose();
            }}
        >
            <CorrectionForm
                key={`${request.sourceField}:${request.submittedValue}`}
                request={request}
                vendorName={vendorName}
                invoiceTypeLabel={invoiceTypeLabel}
                isLoading={isLoading}
                error={error}
                onClose={onClose}
                onConfirm={onConfirm}
            />
        </Box>
    );
}

function CorrectionForm({
    request,
    vendorName,
    invoiceTypeLabel,
    isLoading,
    error,
    onClose,
    onConfirm,
}: {
    request: SubmittedIdentifierCorrectionRequest;
    vendorName: string;
    invoiceTypeLabel: string;
    isLoading: boolean;
    error: string;
    onClose: () => void;
    onConfirm: (correctedValue: string) => void;
}) {
    const [value, setValue] = useState('');
    const submitted = request.submittedValue.trim();
    const isDate = request.sourceField === 'timestamp';

    return (
        <Box
            role="dialog"
            aria-modal="true"
            aria-labelledby="submitted-identifier-correction-title"
            bg="white"
            w="full"
            maxW="440px"
            borderRadius="lg"
            boxShadow="xl"
            p={6}
            onClick={(event) => event.stopPropagation()}
        >
            <Flex align="flex-start" justify="space-between" gap={3} mb={3}>
                <Text
                    id="submitted-identifier-correction-title"
                    fontSize="xl"
                    fontWeight="bold"
                    color="gray.900"
                >
                    Did we get this wrong?
                </Text>
                <CloseButton
                    aria-label="Close correction"
                    onClick={onClose}
                    isDisabled={isLoading}
                />
            </Flex>
            <VStack align="stretch" spacing={3}>
                <Text fontSize="md" color="gray.700">
                    Let us know the correct <strong>{request.fieldLabel}</strong> from the invoice.
                    PartBay will save an AI hint for future <strong>{vendorName}</strong>{' '}
                    <strong>{invoiceTypeLabel}</strong> invoices.
                </Text>
                <Text fontSize="sm" color="gray.600">
                    Current value <strong>{submitted || '(empty)'}</strong>
                </Text>
                <Box>
                    <Input
                        autoFocus
                        value={value}
                        onChange={(event) => setValue(event.target.value)}
                        placeholder={isDate ? 'MM/DD/YYYY' : 'Value printed on the invoice'}
                        isDisabled={isLoading}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && value.trim() && !isLoading) onConfirm(value);
                        }}
                        aria-label={`Correct ${request.fieldLabel}`}
                    />
                    {error ? (
                        <Text mt={2} fontSize="sm" color="red.600">
                            {error}
                        </Text>
                    ) : null}
                </Box>
                <Flex justify="flex-end" gap={3} pt={1}>
                    <Button size="action" variant="outlineGray" onClick={onClose} isDisabled={isLoading}>
                        Not now
                    </Button>
                    <Button
                        size="action"
                        bg="#0071E3"
                        color="white"
                        _hover={{ bg: '#3544a0' }}
                        onClick={() => onConfirm(value)}
                        isLoading={isLoading}
                        isDisabled={!value.trim()}
                    >
                        Save hint
                    </Button>
                </Flex>
            </VStack>
        </Box>
    );
}
